import { entitiesUtils } from '../../entities/entitiesUtils-sidecar'
import { onceOnly } from '../../lib/onceOnly-sidecar'
import { rando } from '../../lib/rando-sidecar'
import type { ISPD } from '../../sprites/sprites'
import { sprites } from '../../sprites/sprites-sidecar'
import { spritesUtil } from '../../sprites/spritesUtil-sidecar'
import { voyageColors } from './voyageColors-sidecar'
import type { IVoyageParticle } from './voyageParticles'

export type VoyageParticleType =
  | 'default'
  | 'fire-extinguisher'
  | 'oxygen'
  | 'blood-crew'
  | 'blood-alien'
  | 'heal'
  | 'heal-hull'
  | 'laser'
  | 'torpedo'
  | 'teleport'
  | 'weapon-hit'
  | 'steam'
  | 'zzz'

export interface IVoyageParticleData {
  type: VoyageParticleType
  frames: ISPD[]
  // bounceOnSurface: boolean
  accelY: number
  minTimeSeconds: number
  maxTimeSeconds: number
  playFrames: boolean
  dirXrange: [number, number]
  dirYrange: [number, number]
  sineX: number
  sineXperiod: number
  scale: number
  afterCreate: ((p: IVoyageParticle) => void) | null
}

const _default: IVoyageParticleData = {
  type: 'default',
  frames: sprites.voyTileParticlesRound,
  // bounceOnSurface: true,
  accelY: 2,
  minTimeSeconds: 0,
  maxTimeSeconds: 2,
  playFrames: false,
  dirXrange: [-1, 1],
  dirYrange: [-0.1, -1],
  sineX: 0,
  sineXperiod: 0,
  afterCreate: null,
  scale: 1,
}

const dataType = 'particles'
const dataList: IVoyageParticleData[] = []
const dataMapType: { [key: string]: IVoyageParticleData } = {}

export const get = (type: VoyageParticleType) => {
  return entitiesUtils.genericGetData(dataType, dataMapType, type)
}

export const registerAll = () => {
  onceOnly.ensureCalledOnlyOnce(dataType)

  register(_default)

  register({
    type: 'fire-extinguisher',
    frames: sprites.voyTileParticlesRound,
    accelY: 0,
    minTimeSeconds: 0.5,
    maxTimeSeconds: 1.25,
    playFrames: true,
    dirXrange: [-0.2, 0.2],
    dirYrange: [-0.1, -0.3],
    sineX: 4,
    sineXperiod: 5,
  })

  register({
    type: 'steam',
    frames: [sprites.voyTileParticlesRound[1]],
    accelY: 0,
    minTimeSeconds: 0.4,
    maxTimeSeconds: 0.9,
    playFrames: false,
    dirXrange: [-0.2, 0.2],
    dirYrange: [-0.1, -0.3],
    sineX: 4,
    sineXperiod: 5,
    afterCreate: (p) => {
      spritesUtil.scale(p.image, 1)
      p.fade = true
    },
  })

  register({
    type: 'zzz',
    frames: sprites.voyTileParticlesZ,
    accelY: 0,
    minTimeSeconds: 1,
    maxTimeSeconds: 2,
    playFrames: false,
    dirXrange: [-0.2, 0.2],
    dirYrange: [-0.1, -0.2],
    sineX: 4,
    sineXperiod: 5,
    afterCreate: (p) => {
      spritesUtil.scale(p.image, 1)
      // p.image.tint = voyageColors.red
      p.fade = true
    },
  })

  register({
    type: 'oxygen',
    frames: sprites.voyTileParticlesCircle,
    accelY: 0,
    minTimeSeconds: 0.5,
    maxTimeSeconds: 1.25,
    playFrames: true,
    dirXrange: [-0.2, 0.2],
    dirYrange: [-0.1, -0.3],
    sineX: 4,
    sineXperiod: 5,

    afterCreate: (p) => {
      p.fade = true
    },
  })

  register({
    type: 'heal',
    frames: [sprites.voyTilePluses[1]],
    accelY: 0,
    minTimeSeconds: 1,
    maxTimeSeconds: 2.0,
    playFrames: false,
    dirXrange: [-0.2, 0.2],
    dirYrange: [-0.1, -0.3],
    sineX: 4,
    sineXperiod: 5,
    //scale: 0.25,

    afterCreate: (p) => {
      p.image.tint = voyageColors.green
      spritesUtil.scale(p.image, 1)
      p.fade = true
    },
  })

  register({
    type: 'teleport',
    frames: [sprites.voyTileParticlesRound[1]],
    accelY: 0,
    minTimeSeconds: 1,
    maxTimeSeconds: 2.0,
    playFrames: false,
    dirXrange: [-0.3, 0.3],
    dirYrange: [-0.1, -0.9],
    sineX: 4,
    sineXperiod: 5,
    //scale: 0.25,

    afterCreate: (p) => {
      p.image.tint = voyageColors.green
      spritesUtil.scale(p.image, 2)
      p.fade = true
    },
  })

  register({
    type: 'weapon-hit',
    frames: [sprites.voyTileParticlesRound[1]],
    accelY: 0,
    minTimeSeconds: 1.5,
    maxTimeSeconds: 4.0,
    playFrames: false,
    dirXrange: [-0.5, 0.5],
    dirYrange: [-0.2, -0.9],
    sineX: 4,
    sineXperiod: 5,
    //scale: 0.25,

    afterCreate: (p) => {
      //p.image.tint = voyageColors.green
      spritesUtil.scale(p.image, rando.i(1, 6))
      p.fade = true
    },
  })

  register({
    type: 'heal-hull',
    frames: [sprites.voyTilePluses[1]],
    accelY: 0,
    minTimeSeconds: 1,
    maxTimeSeconds: 2.0,
    playFrames: false,
    dirXrange: [-0.2, 0.2],
    dirYrange: [-0.1, -0.3],
    sineX: 4,
    sineXperiod: 5,
    //scale: 0.25,

    afterCreate: (p) => {
      p.image.tint = voyageColors.g3
      spritesUtil.scale(p.image, 1)
      p.fade = true
    },
  })

  register({
    type: 'laser',
    frames: [sprites.voyTileParticlesRound[0]],
    accelY: 0,
    minTimeSeconds: 4,
    maxTimeSeconds: 4,
    playFrames: true,
    dirXrange: [0, 0],
    dirYrange: [0, 0],
    afterCreate: (p) => {
      p.image.tint = voyageColors.red
      p.dirX = 10
    },
  })

  register({
    type: 'torpedo',
    frames: [sprites.voyItemTorpedo[0]],
    accelY: 0,
    minTimeSeconds: 4,
    maxTimeSeconds: 4,
    playFrames: true,
    dirXrange: [0, 0],
    dirYrange: [0, 0],
    afterCreate: (p) => {
      // p.image.tint = voyageColors.damage
      p.dirX = 5
      p.image.flipX = true
    },
  })

  register({
    type: 'blood-crew',
    frames: sprites.voyTileParticlesRound,
    accelY: 0.5,
    minTimeSeconds: 0.5,
    maxTimeSeconds: 1.75,
    playFrames: true,
    dirXrange: [-0.2, 0.2],
    dirYrange: [-0.1, -0.3],
    afterCreate: (p) => {
      p.image.tint = voyageColors.red
    },
  })

  register({
    type: 'blood-alien',
    frames: sprites.voyTileParticlesRound,
    accelY: 0.5,
    minTimeSeconds: 0.5,
    maxTimeSeconds: 1.75,
    playFrames: true,
    dirXrange: [-0.2, 0.2],
    dirYrange: [-0.1, -0.3],
    afterCreate: (p) => {
      p.image.tint = voyageColors.green
    },
  })
}
export const register = (data: Partial<IVoyageParticleData>) => {
  entitiesUtils.genericRegisterData(
    dataType,
    data,
    _default,
    dataList,
    dataMapType,
  )
}
